import { useParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { dayCareApi } from '@/api/daycare';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Activity, ArrowLeft, TestTube, Pill, ClipboardList, IndianRupee, Receipt } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import type { DayCareStatus } from '@/types/daycare';
import { toast } from 'sonner';
import { useCanMutateFrontDeskIpd, useFrontDeskBasePath } from '@/hooks/useFrontDeskBasePath';

function formatDateTime(value?: string | null) {
  if (!value) return '—';
  const d = new Date(value);
  return format(d, 'dd/MM/yyyy HH:mm');
}

function statusLabel(status?: DayCareStatus | null) {
  if (status == null || typeof status !== 'string') return '—';
  return status.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
}

export default function DayCareCasePage() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const basePath = useFrontDeskBasePath();
  const canMutateIpd = useCanMutateFrontDeskIpd();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['daycare-case', caseId],
    enabled: !!caseId,
    queryFn: async () => {
      const res = await dayCareApi.getCaseById(caseId!);
      return res.data?.data;
    },
  });

  const createBillMutation = useMutation({
    mutationFn: async () => {
      if (!caseId) throw new Error('Missing case id');
      const res = await dayCareApi.getOrCreateBill(caseId);
      return res.data?.data;
    },
    onSuccess: () => {
      toast.success('Day Care bill ready');
      queryClient.invalidateQueries({ queryKey: ['daycare-case', caseId] });
    },
    onError: (error: any) => {
      console.error('Failed to create Day Care bill', error);
      toast.error(error?.response?.data?.message || 'Failed to create Day Care bill');
    },
  });

  if (!caseId) {
    return <div className="p-6">Invalid Day Care case id</div>;
  }

  const c: any = data;

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(-1)}
            className="shrink-0"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-artis-serif text-2xl font-semibold text-artis-brown-dark">
              Day Care Case
            </h1>
            <p className="text-sm text-gray-600">
              {c?.patient?.name || '—'} · {c?.procedure?.name || '—'}
            </p>
          </div>
        </div>
        <Badge variant="outline" className="text-sm border-blue-200 bg-blue-50 text-blue-700">
          {statusLabel(c?.status)}
        </Badge>
      </div>

      {isLoading ? (
        <div className="p-6 text-gray-500">Loading Day Care case...</div>
      ) : !c ? (
        <div className="p-6 text-gray-500">Day Care case not found.</div>
      ) : (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4 text-blue-600" />
                Overview
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Patient</div>
                  <div className="font-medium">{c.patient?.name}</div>
                  <div className="text-xs text-gray-500">
                    UHID: {c.patient?.uhid || '—'} · {c.patient?.phone}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Procedure</div>
                  <div className="font-medium">{c.procedure?.name}</div>
                </div>
                <div>
                  <div className="text-gray-500">Doctor</div>
                  <div className="font-medium">{c.doctor?.name}</div>
                </div>
                <div>
                  <div className="text-gray-500">Scheduled At</div>
                  <div className="font-medium">{formatDateTime(c.scheduledAt)}</div>
                </div>
                <div>
                  <div className="text-gray-500">Resource</div>
                  <div className="font-medium">{c.resource?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Nurse</div>
                  <div className="font-medium">{c.nurse?.name || '—'}</div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <IndianRupee className="h-4 w-4 text-emerald-600" />
                Billing
              </CardTitle>
              {canMutateIpd ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => createBillMutation.mutate()}
                  disabled={createBillMutation.isPending}
                >
                  {c.bill ? 'Refresh Bill' : 'Generate Bill'}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent>
              {c.bill?.bill ? (
                <div className="text-sm space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Bill Number</span>
                    <span className="font-medium">{c.bill.bill.billNumber}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Total</span>
                    <span className="font-medium">
                      ₹{(c.bill.bill.totalAmount ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Paid</span>
                    <span className="font-medium">
                      ₹{(c.bill.bill.paidAmount ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Due</span>
                    <span className="font-medium">
                      ₹{(c.bill.bill.dueAmount ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 flex items-center gap-1"
                      onClick={() => navigate(`${basePath}/daycare/${c.id}/billing`)}
                    >
                      <Receipt className="h-3 w-3" />
                      Open detailed bill
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-artis-slate">
                  No bill yet. Click &quot;Generate Bill&quot; to create a Day Care bill
                  based on the procedure charge.
                </p>
              )}
            </CardContent>
          </Card>

          <Tabs defaultValue="timeline" className="space-y-4">
            <TabsList>
              <TabsTrigger value="timeline">Timeline</TabsTrigger>
              <TabsTrigger value="vitals">Vitals</TabsTrigger>
              <TabsTrigger value="medicines">Medicines</TabsTrigger>
              <TabsTrigger value="lab">Lab</TabsTrigger>
              <TabsTrigger value="notes">Notes</TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Status timeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 text-sm">
                    {c.statusLogs?.map((log: any) => (
                      <li key={log.id} className="flex items-start gap-2">
                        <span className="mt-1 h-2 w-2 rounded-full bg-blue-500" />
                        <div>
                          <div className="font-medium">
                            {statusLabel(log.toStatus as DayCareStatus)}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDateTime(log.createdAt)} ·{' '}
                            {log.changedBy?.name || 'System'}
                          </div>
                          {log.reason && (
                            <div className="text-xs thms-page-subtitle">
                              {log.reason}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="vitals">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Activity className="h-4 w-4" />
                    Vitals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-artis-slate">
                    Vitals captured for this Day Care case will appear here. (Backend link
                    already supports `dayCareCaseId`; UI integration can be expanded later.)
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="medicines">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <Pill className="h-4 w-4" />
                    Medicines & consumables
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-artis-slate">
                    Pharmacy issues linked to this case will be surfaced here in a later
                    iteration.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="lab">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <TestTube className="h-4 w-4" />
                    Lab
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-artis-slate">
                    Lab orders linked via `dayCareCaseId` will be shown here once the lab
                    UI is wired to Day Care.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="notes">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <ClipboardList className="h-4 w-4" />
                    Clinical & nursing notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-artis-slate">
                    Notes from doctors and nurses will be listed here. Backing model
                    (`DayCareNote`) is ready; UI for capturing notes can be added next.
                  </p>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}

